import { Box, Button, Flex, Image, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { MouseEvent, useMemo, useState } from 'react'
import { FiEdit3, FiEye, FiImage } from 'react-icons/fi'

import { ActionButton } from '../../../components/ActionButton'
import { SectionCard } from '../../../components/SectionCard'
import { useAppToast } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import {
  IArtworkShowcaseState,
  IContestArtworkForEvaluation,
  IContestEvaluationCriterion,
  IShowcaseTakenPosition
} from '../../../services/Contests/interfaces'
import { resolveArtMeta } from '../Contests/artFieldHelpers'
import ArtMetaInline from '../Contests/ArtMetaInline'
import ArtworkShowcaseControl from './ArtworkShowcaseControl'
import GradeBar from './GradeBar'

interface IArtworkEvaluationCardProps {
  artwork: IContestArtworkForEvaluation
  criteria: IContestEvaluationCriterion[]
  index: number
  onEdit: (artwork: IContestArtworkForEvaluation) => void
  /** ABRA-314 — os slots da vitrine já tomados no concurso. */
  takenPositions: IShowcaseTakenPosition[]
  /** ABRA-314 — quantos slots a vitrine oferece, como a API informa. */
  maxShowcasePosition: number
  /** ABRA-314 — chamado quando a curadoria da vitrine muda, já confirmada pela API. */
  onShowcaseChanged: (artId: number, state: IArtworkShowcaseState, wasSelected: boolean) => void
  /** view_contest_votes: the card opens the modal to look at grades, not to edit them. */
  readOnly?: boolean
}

const MotionSectionCard = motion(SectionCard)

function averagePerCriterion(
  evaluations: IContestArtworkForEvaluation['evaluations'],
  criterionId: number
): number | null {
  const values = evaluations
    .map((e) => e.criterion_grades.find((g) => g.contest_evaluation_criterion_id === criterionId)?.grade)
    .filter((v): v is number => v !== null && v !== undefined)
  if (values.length === 0) return null
  return values.reduce((acc, v) => acc + v, 0) / values.length
}

export default function ArtworkEvaluationCard({
  artwork,
  criteria,
  index,
  onEdit,
  takenPositions,
  maxShowcasePosition,
  onShowcaseChanged,
  readOnly = false
}: IArtworkEvaluationCardProps) {
  const service = useMemo(() => new ContestsService(), [])
  const toast = useAppToast()
  const [isDownloading, setIsDownloading] = useState(false)

  const grade = artwork.overall_average
  const isGraded = artwork.evaluation_status === 'evaluated'
  // Magenta is the call to action "go grade this"; a viewer never has one.
  const isNeutral = isGraded || readOnly
  const hasAnyGrade = grade !== null && grade !== undefined

  // ABRA-297 — o card inteiro abre a avaliação, então o clique no download
  // não pode subir junto.
  const handleDownload = async (event: MouseEvent) => {
    event.stopPropagation()
    setIsDownloading(true)
    try {
      await service.downloadArtworkImage(artwork.id, `obra-${artwork.id}.jpg`)
    } catch (error) {
      toast.error('Erro ao baixar a imagem da obra', error)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <MotionSectionCard
      variant="flush"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04, ease: [0.22, 0.61, 0.36, 1] }}
      cursor="pointer"
      onClick={() => onEdit(artwork)}
      sx={{
        transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
        ':hover': {
          transform: 'translateY(-3px)',
          borderColor: 'gray.300',
          boxShadow: '0 12px 32px -12px rgba(17,24,39,0.15)'
        },
        ':hover .abra-art-image': { transform: 'scale(1.04)' },
        ':hover .abra-art-edit': { opacity: 1, transform: 'translateY(0)' }
      }}
    >
      {/* Image */}
      <Box position="relative" overflow="hidden" bg="gray.100" h="200px">
        <Box
          className="abra-art-image"
          w="100%"
          h="100%"
          sx={{ transition: 'transform .6s cubic-bezier(.22,.61,.36,1)' }}
        >
          <Image
            src={artwork.art_image_url || ''}
            alt={artwork.name}
            w="100%"
            h="100%"
            objectFit="cover"
            display="block"
            fallback={
              <Flex
                w="100%"
                h="100%"
                bg="gray.100"
                align="center"
                justify="center"
                direction="column"
                gap="2"
                color="gray.400"
              >
                <Box fontSize="28px">
                  <FiImage />
                </Box>
                <Text fontSize="xs">Sem imagem</Text>
              </Flex>
            }
          />
        </Box>

        {/* Status pill */}
        <Box position="absolute" top="10px" left="10px">
          <Flex
            align="center"
            gap="1.5"
            px="2.5"
            py="1"
            borderRadius="full"
            bg={isGraded ? 'rgba(16,185,129,0.95)' : 'rgba(255,255,255,0.95)'}
            color={isGraded ? 'white' : 'gray.700'}
            fontSize="11px"
            fontWeight="600"
            backdropFilter="blur(8px)"
            boxShadow="0 2px 8px rgba(0,0,0,0.08)"
          >
            <Box w="6px" h="6px" borderRadius="full" bg={isGraded ? 'white' : 'orange.400'} />
            {isGraded ? 'Avaliada' : 'Pendente'}
          </Flex>
        </Box>

        {/* Grade badge */}
        {hasAnyGrade && (
          <Flex
            position="absolute"
            top="10px"
            right="10px"
            align="center"
            justify="center"
            minW="44px"
            h="28px"
            px="2.5"
            borderRadius="full"
            bg="white"
            color="gray.900"
            fontSize="13px"
            fontWeight="700"
            boxShadow="0 2px 8px rgba(0,0,0,0.08)"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {grade!.toFixed(1)}
          </Flex>
        )}

        {/* Download */}
        {artwork.art_image_url && (
          <Box position="absolute" bottom="10px" left="10px">
            <ActionButton
              action="download"
              label="Baixar imagem"
              onClick={handleDownload}
              isLoading={isDownloading}
              mr="0"
              w="32px"
              minW="32px"
              h="32px"
              borderRadius="full"
              variant="solid"
              colorScheme="gray"
              bg="rgba(255,255,255,0.95)"
              color="gray.700"
              boxShadow="0 2px 8px rgba(0,0,0,0.12)"
              backdropFilter="blur(8px)"
              _hover={{ bg: 'white', color: 'magenta.500' }}
            />
          </Box>
        )}

        {/* Hover edit button */}
        <Flex
          className="abra-art-edit"
          position="absolute"
          bottom="10px"
          right="10px"
          align="center"
          gap="1.5"
          px="3"
          py="2"
          borderRadius="10px"
          bg="gray.900"
          color="white"
          fontSize="12px"
          fontWeight="600"
          opacity="0"
          pointerEvents="none"
          boxShadow="0 8px 20px -4px rgba(0,0,0,0.3)"
          sx={{
            transition: 'all .2s cubic-bezier(.22,.61,.36,1)',
            transform: 'translateY(6px)'
          }}
        >
          {readOnly ? <FiEye size={14} /> : <FiEdit3 size={14} />}
          {readOnly ? 'Ver' : isGraded ? 'Editar' : 'Avaliar'}
        </Flex>
      </Box>

      {/* Body */}
      <Box p="4">
        <Text
          fontSize="15px"
          fontWeight="600"
          color="gray.900"
          noOfLines={2}
          mb="0.5"
          letterSpacing="-0.01em"
          title={artwork.name}
        >
          {artwork.name}
        </Text>
        <Text variant="caption" noOfLines={1}>
          {artwork.applicant_name || 'Artista desconhecido'}
        </Text>
        {artwork.work_type && (
          <Text variant="caption" noOfLines={1}>
            Tipo: {artwork.work_type}
          </Text>
        )}
        {(() => {
          const meta = resolveArtMeta(artwork)
          return meta.length > 0 ? (
            <ArtMetaInline meta={meta} fontSize="12px" color="gray.400" noOfLines={1} mb="4" />
          ) : (
            <Box mb="4" />
          )
        })()}

        <ArtworkShowcaseControl
          artwork={artwork}
          takenPositions={takenPositions}
          maxPosition={maxShowcasePosition}
          readOnly={readOnly}
          onChanged={onShowcaseChanged}
        />

        <VStack spacing="2.5" align="stretch" mb="4">
          {criteria.map((criterion) => (
            <GradeBar
              key={criterion.id}
              label={criterion.name}
              tooltip={`Peso ${criterion.weight}%`}
              value={averagePerCriterion(artwork.evaluations, criterion.id)}
            />
          ))}
        </VStack>

        <Button
          w="100%"
          h="38px"
          size="sm"
          bg={isNeutral ? 'gray.100' : 'magenta.500'}
          color={isNeutral ? 'gray.700' : 'white'}
          fontSize="13px"
          fontWeight="600"
          borderRadius="10px"
          leftIcon={readOnly ? <FiEye size={14} /> : <FiEdit3 size={14} />}
          onClick={(e) => {
            e.stopPropagation()
            onEdit(artwork)
          }}
          _hover={{ bg: isNeutral ? 'gray.200' : 'magenta.600' }}
          _active={{ transform: 'scale(0.98)' }}
          sx={{ transition: 'all .15s ease' }}
        >
          {readOnly ? 'Ver avaliação' : isGraded ? 'Revisar avaliação' : 'Avaliar obra'}
        </Button>
      </Box>
    </MotionSectionCard>
  )
}
